/**
 * Patient Registration Wizard Component
 *
 * Main wizard component that manages the multi-step patient registration workflow.
 * Handles step navigation, progress tracking, loading states, and error handling.
 * Integrates with existing UI components and theme system.
 * Follows the exact pattern from DieticianRegistrationWizard.
 *
 * @module components/patient-registration/PatientRegistrationWizard
 */

import React, { useEffect } from 'react';
import { Card, CardBody } from '@/components/ui/Card';
import { StepIndicator, type StepConfig } from '@/components/ui/StepIndicator';
import { usePatientRegistrationFlow } from '@/hooks/usePatientRegistrationFlow';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

// Step components
import { PatientRegistrationForm } from './PatientRegistrationForm';
import { PatientProfileForm } from './PatientProfileForm';

export interface PatientRegistrationWizardProps {
  className?: string;
  onComplete?: (registrationId: string) => void;
  onStepChange?: (step: number) => void;
}

// Simple icon components for steps
const UserPlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const UserCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);


const WIZARD_STEPS: StepConfig[] = [
  {
    key: '1',
    title: 'Registration',
    description: 'Create your account',
    icon: <UserPlusIcon />
  },
  {
    key: '2',
    title: 'Profile',
    description: 'Personal information',
    icon: <UserCircleIcon />
  }
];

export function PatientRegistrationWizard(props: PatientRegistrationWizardProps) {
  // Hooks
  const { isDark } = useTheme();
  const registrationFlow = usePatientRegistrationFlow();

  // Destructure for cleaner code
  const { state, actions, validation } = registrationFlow;
  const { currentStep, isLoading, errors, isSubmitting } = state;
  const { setCurrentStep, submitRegistration, submitProfile } = actions;

  // Effect: Notify parent of step changes
  useEffect(() => {
    props.onStepChange?.(currentStep);
  }, [currentStep, props.onStepChange]);

  // Effect: Notify parent of completion
  useEffect(() => {
    if (state.completedSteps.has(2)) {
      props.onComplete?.(state.registrationId || 'completed');
    }
  }, [state.completedSteps, state.registrationId, props.onComplete]);

  // Step navigation handlers
  const handleStepClick = (stepKey: string) => {
    const stepId = parseInt(stepKey, 10);
    // Only allow navigation to completed steps or next step
    if (validation.canProceedToStep(stepId) || stepId === currentStep) {
      setCurrentStep(stepId);
    }
  };

  // Render current step content
  const renderStepContent = (): React.ReactElement => {
    switch (currentStep) {
      case 1:
        return (
          <PatientRegistrationForm
            onSubmit={submitRegistration}
            initialData={state.formData}
            isLoading={isSubmitting}
            errors={errors}
          />
        );

      case 2:
        return (
          <PatientProfileForm
            onSubmit={submitProfile}
            initialData={state.formData}
            isLoading={isSubmitting}
            errors={errors}
          />
        );

      default:
        return <div>Invalid step</div>;
    }
  };

  // Main render
  return (
    <div className={cn('max-w-4xl mx-auto p-6', props.className)}>
      {/* Progress indicator */}
      <div className="mb-8">
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep.toString()}
          onStepClick={handleStepClick}
          variant="horizontal"
        />
      </div>

      {/* Main content card */}
      <div className="flex justify-center">
        <Card className={cn(
          'w-full max-w-2xl shadow-lg transition-all duration-300',
          isDark ? 'bg-secondary-900' : 'bg-white'
        )}>
          <CardBody className="p-8">
            {/* Step title and description */}
            <div className="mb-6">
              <h2 className={cn(
                'text-2xl font-bold mb-2',
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              )}>
                {WIZARD_STEPS[currentStep - 1].title}
              </h2>
              <p className={cn(
                'text-lg',
                isDark ? 'text-secondary-400' : 'text-secondary-600'
              )}>
                {WIZARD_STEPS[currentStep - 1].description}
              </p>
            </div>

            {/* Loading overlay */}
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                <div className="bg-white p-4 rounded-lg shadow-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-600">Processing...</p>
                </div>
              </div>
            )}

            {/* Step content */}
            <div className="relative">
              {renderStepContent()}
            </div>

          </CardBody>
        </Card>
      </div>

    </div>
  );
}